export type VitalsChecklistItem =
  | "weight"
  | "height"
  | "temperature"
  | "pulseRate"
  | "heartRate"
  | "spo2"
  | "glucoseLevel"
  | "respiratoryRate"
  | "bloodPressure"
  | "patientConcerns"
  | "ongoingMedication"
  | "pregnancyMonths"
  | "fetalHeartRate"
  | "vegetarian";

export interface IDoctorPrescriptionData {
  id?: string;
  doctorId: string;
  doctorName: string;
  specialisations?: string[];
  education?: string;
  registrationNumber?: string;
  vitalsChecklist?: string[];
  selfClinicAddress?: string;
  selfClinicMobile?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ICreateDoctorPrescriptionDataDTO {
  doctorId: string;
  doctorName: string;
  specialisations?: string[];
  education?: string;
  registrationNumber?: string;
  vitalsChecklist?: string[];
  selfClinicAddress?: string;
  selfClinicMobile?: string;
}

export interface IUpdateDoctorPrescriptionDataDTO {
  doctorName?: string;
  specialisations?: string[];
  education?: string;
  registrationNumber?: string;
  vitalsChecklist?: string[];
  selfClinicAddress?: string;
  selfClinicMobile?: string;
}

export interface VitalMetadata {
  key: VitalsChecklistItem;
  label: string;
  shortLabel: string;
  unit: string;
  sampleValue: string;
  category: "General" | "Cardiovascular" | "Maternal & Women" | "History & Lifestyle";
  description?: string;
}

export const ALL_VITALS_CONFIG: VitalMetadata[] = [
  {
    key: "weight",
    label: "Weight",
    shortLabel: "Weight",
    unit: "kg",
    sampleValue: "97 kg",
    category: "General",
    description: "Body weight in kilograms",
  },
  {
    key: "height",
    label: "Height",
    shortLabel: "Height",
    unit: "cm",
    sampleValue: "175 cm",
    category: "General",
    description: "Body height in centimeters",
  },
  {
    key: "temperature",
    label: "Body Temperature",
    shortLabel: "Temp",
    unit: "°C",
    sampleValue: "97 °C",
    category: "General",
    description: "Body temperature in Celsius/Fahrenheit",
  },
  {
    key: "pulseRate",
    label: "Pulse Rate",
    shortLabel: "Pulse",
    unit: "bpm",
    sampleValue: "91 bpm",
    category: "Cardiovascular",
    description: "Pulse rate per minute",
  },
  {
    key: "heartRate",
    label: "Heart Rate",
    shortLabel: "Heart Rate",
    unit: "bpm",
    sampleValue: "78 bpm",
    category: "Cardiovascular",
    description: "Resting heart rate in beats per minute",
  },
  {
    key: "spo2",
    label: "SpO2 (Oxygen Saturation)",
    shortLabel: "SpO2",
    unit: "%",
    sampleValue: "98%",
    category: "Cardiovascular",
    description: "Blood oxygen saturation percentage",
  },
  {
    key: "glucoseLevel",
    label: "Blood Glucose Level",
    shortLabel: "Blood Sugar",
    unit: "mg/dL",
    sampleValue: "110 mg/dL",
    category: "General",
    description: "Fasting or random blood glucose level",
  },
  {
    key: "respiratoryRate",
    label: "Respiratory Rate",
    shortLabel: "Resp Rate",
    unit: "bpm",
    sampleValue: "18 bpm",
    category: "General",
    description: "Breathing rate per minute",
  },
  {
    key: "bloodPressure",
    label: "Blood Pressure (BP)",
    shortLabel: "BP",
    unit: "mmHg",
    sampleValue: "120/80 mmHg",
    category: "Cardiovascular",
    description: "Systolic & diastolic blood pressure",
  },
  {
    key: "patientConcerns",
    label: "Patient Concerns / Complaints",
    shortLabel: "Concerns",
    unit: "",
    sampleValue: "Fever & Headaches",
    category: "History & Lifestyle",
    description: "Primary chief complaints of the patient",
  },
  {
    key: "ongoingMedication",
    label: "Ongoing Medication",
    shortLabel: "Ongoing Meds",
    unit: "",
    sampleValue: "Tab Paracetamol 650mg",
    category: "History & Lifestyle",
    description: "Current prescriptions and active medications",
  },
  {
    key: "pregnancyMonths",
    label: "Pregnancy (Months)",
    shortLabel: "Pregnancy",
    unit: "Months",
    sampleValue: "6 Months",
    category: "Maternal & Women",
    description: "Gestational age in months if applicable",
  },
  {
    key: "fetalHeartRate",
    label: "Fetal Heart Rate (FHR)",
    shortLabel: "Fetal HR",
    unit: "bpm",
    sampleValue: "140 bpm",
    category: "Maternal & Women",
    description: "Fetal heart rate in beats per minute",
  },
  {
    key: "vegetarian",
    label: "Diet Preference",
    shortLabel: "Diet",
    unit: "",
    sampleValue: "Vegetarian",
    category: "History & Lifestyle",
    description: "Dietary habits (Vegetarian / Non-Vegetarian)",
  },
];

export const DEFAULT_VITALS_CHECKLIST: VitalsChecklistItem[] = [
  "weight",
  "temperature",
  "pulseRate",
  "bloodPressure",
  "spo2",
];
